import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { InheritanceType } from '../types';
import { GLASSMORPHIC_COLORS, getRandomGlassmorphicColorExcluding } from '../utils/colorPalette';
import { trackEvent } from '../utils/analytics';
import './BranchCreationModal.css';

interface BranchCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateBranch: (color: string, inheritanceType: InheritanceType) => Promise<void>;
  branchPoint: number;
  parentTopic: string;
  messageContent: string;
  isLoading?: boolean;
  parentColor?: string; // Add parent color to avoid using the same color
}

const INHERITANCE_OPTIONS = [
  {
    type: 'full' as InheritanceType,
    title: 'Full Context',
    description: 'Inherit the entire conversation history from the parent up to this point. AI will generate a topic for the new branch.',
    icon: '📚'
  },
  {
    type: 'summary' as InheritanceType,
    title: 'Summary Context',
    description: 'AI will summarize the parent conversation and the branch inherits only that summary.',
    icon: '📝'
  },
  {
    type: 'empty' as InheritanceType,
    title: 'Empty Context',
    description: 'Start a fresh conversation with no inherited context.',
    icon: '🆕'
  }
];

const BranchCreationModal: React.FC<BranchCreationModalProps> = ({
  isOpen,
  onClose,
  onCreateBranch,
  branchPoint,
  parentTopic,
  messageContent,
  isLoading = false,
  parentColor
}) => {
  // Initialize with a glassmorphic color different from parent, or first color if no parent
  const initialGlassmorphicColor = parentColor ? getRandomGlassmorphicColorExcluding(parentColor) : GLASSMORPHIC_COLORS[0];
  const [selectedColor, setSelectedColor] = useState<string>(initialGlassmorphicColor.solid);
  const [selectedInheritance, setSelectedInheritance] = useState<InheritanceType>('full');
  const modalRef = useRef<HTMLDivElement>(null);

  // Get loading message based on inheritance type
  const getLoadingMessage = () => {
    switch (selectedInheritance) {
      case 'full':
        return 'Generating topic and creating branch...';
      case 'summary':
        return 'Summarizing conversation, generating topic, and creating branch...';
      case 'empty':
        return 'Creating branch...';
      default:
        return 'Creating branch...';
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleCreateBranch = async () => {
    await onCreateBranch(selectedColor, selectedInheritance);
    trackEvent('branch_created', { new_branch_name: parentTopic });
    onClose();
  };



  if (!isOpen) return null;

  const modal = (
    <div className="branch-modal-overlay">
      <div className="branch-modal" ref={modalRef}>
        <div className="branch-modal-header">
          <h3>Create Branch from "{parentTopic}"</h3>
          <button className="branch-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="branch-modal-content">
          {isLoading && (
            <div className="branch-loading-indicator">
              <div className="loading-spinner"></div>
              <p>{getLoadingMessage()}</p>
            </div>
          )}
          
          <div className="branch-point-info">
            <div className="branch-point-label">
              <span className="branch-icon">🌿</span>
              Branching from: "{messageContent.length > 80 ? messageContent.substring(0, 80) + '...' : messageContent}"
            </div>
          </div>

          <div className="color-selection-section">
            <h4>Choose Branch Color</h4>
            <div className="color-grid">
              {GLASSMORPHIC_COLORS.map((color) => (
                <button
                  key={color.id}
                  className={`color-option ${selectedColor === color.solid ? 'selected' : ''}`}
                  style={{ 
                    background: color.bg,
                    border: `2px solid ${color.border}`,
                    borderRadius: '12px',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    width: '32px',
                    height: '28px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setSelectedColor(color.solid)}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          <div className="inheritance-selection-section">
            <h4>Choose Context Inheritance</h4>
            <div className="inheritance-options">
              {INHERITANCE_OPTIONS.map((option) => (
                <button
                  key={option.type}
                  className={`inheritance-option ${selectedInheritance === option.type ? 'selected' : ''}`}
                  onClick={() => setSelectedInheritance(option.type)}
                >
                  <div className="inheritance-icon">{option.icon}</div>
                  <div className="inheritance-content">
                    <div className="inheritance-title">{option.title}</div>
                    <div className="inheritance-description">{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="branch-modal-footer">
          <button className="cancel-button" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button 
            className="create-button" 
            onClick={handleCreateBranch}
            disabled={isLoading}
          >
            {isLoading ? 'Creating Branch...' : 'Create Branch'}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default BranchCreationModal; 